/**
 * MaintenanceBanner — the SCHEDULED pre-announcement. Non-blocking by design:
 * the app stays fully usable, this only warns that a window is coming.
 *
 * Deliberate choices
 * ------------------
 * - ``role="status"`` (polite), never ``alert``: an upcoming window must not
 *   hijack a screen reader mid-task (WCAG 4.1.3).
 * - Dismissible — but the dismissal is keyed to the WINDOW, so rescheduling
 *   re-announces instead of staying silently hidden.
 * - The countdown only appears in the final hours; a live ticker days out is
 *   theatre, the localized date is what actually helps.
 */

import { useCallback, useState, type ReactElement } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { X } from "lucide-react";
import { Trans, useLingui } from "@lingui/react/macro";

import { useCountdown, type MaintenanceRecord } from "./useMaintenance";

const EASE = [0.32, 0.72, 0, 1] as const;

/** Show the live ticker only inside this horizon. */
const COUNTDOWN_HORIZON_MS = 6 * 60 * 60 * 1000;

const dismissKey = (record: MaintenanceRecord) =>
  `ando.maintenance.dismissed:${record.starts_at ?? "immediate"}`;

export interface MaintenanceBannerProps {
  readonly record: MaintenanceRecord;
}

export function MaintenanceBanner({
  record,
}: MaintenanceBannerProps): ReactElement | null {
  const { i18n, t } = useLingui();
  const still = useReducedMotion() ?? false;
  const countdown = useCountdown(record.starts_at);

  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(dismissKey(record)) === "1";
    } catch {
      return false;
    }
  });

  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      sessionStorage.setItem(dismissKey(record), "1");
    } catch {
      // Private-mode storage denial must not break the dismissal.
    }
  }, [record]);

  if (dismissed) return null;

  const startsAt = record.starts_at ? new Date(record.starts_at) : null;
  const windowLabel =
    startsAt && !Number.isNaN(startsAt.getTime())
      ? new Intl.DateTimeFormat(i18n.locale, {
          day: "numeric",
          month: "short",
          hour: "2-digit",
          minute: "2-digit",
        }).format(startsAt)
      : null;

  const imminent = countdown !== null && countdown.totalMs <= COUNTDOWN_HORIZON_MS;

  return (
    <AnimatePresence>
      <motion.div
        role="status"
        aria-live="polite"
        initial={still ? false : { y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -40, opacity: 0 }}
        transition={{ duration: 0.7, ease: EASE }}
        className="relative z-30 border-hairline border-b bg-cream/90 backdrop-blur-sm"
      >
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-2.5">
          {/* Breathing accent dot — the one orange moment, not a filled bar. */}
          <span className="relative flex h-2 w-2 shrink-0" aria-hidden>
            {!still ? (
              <motion.span
                className="absolute inline-flex h-full w-full rounded-full bg-accent"
                animate={{ scale: [1, 2.4], opacity: [0.5, 0] }}
                transition={{ duration: 2.2, ease: "easeOut", repeat: Infinity }}
              />
            ) : null}
            <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
          </span>

          <p className="min-w-0 flex-1 text-[12.5px] text-ink-1">
            <span className="font-medium">
              <Trans>Manutenzione programmata</Trans>
            </span>
            {windowLabel ? (
              <span className="text-ink-2">
                {" · "}
                {windowLabel}
              </span>
            ) : null}
            {record.message?.trim() ? (
              <span className="hidden text-ink-2 sm:inline">
                {" — "}
                {record.message}
              </span>
            ) : null}
          </p>

          {imminent && countdown ? (
            <span className="hidden shrink-0 items-center gap-1 rounded-full border border-hairline bg-card px-2.5 py-1 font-medium text-[11px] text-ink-1 tabular-nums sm:flex">
              <Trans>tra</Trans>{" "}
              {countdown.hours > 0
                ? `${countdown.hours}h ${String(countdown.minutes).padStart(2, "0")}m`
                : `${countdown.minutes}m ${String(countdown.seconds).padStart(2, "0")}s`}
            </span>
          ) : null}

          <button
            type="button"
            onClick={dismiss}
            aria-label={t`Nascondi avviso di manutenzione`}
            className="-mr-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-ink-3 transition-all duration-400 ease-[cubic-bezier(0.32,0.72,0,1)] hover:bg-cream-deep hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 active:scale-90"
          >
            <X size={13} aria-hidden />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
